import { http } from "../http"

class LoyaltyFetchApi {
    static async fetchCampaignGroups() {
        return (await http.get("/loyalty/customer-groups")).data.result
    }

    static async previewCampaignAudience(id) {
        return (await http.post(`/loyalty/campaigns/${id}/audience-preview`, {})).data.result
    }

    static async issueCampaignAudience(id, payload) {
        return (await http.post(`/loyalty/campaigns/${id}/issue`, payload)).data.result
    }

    static async fetchCampaignIssuances(id) {
        return (await http.get(`/loyalty/campaigns/${id}/issuances`)).data.result
    }

    static async uploadCampaignArtwork(form) {
        return (await http.post("/loyalty/campaign-artwork", form)).data.result
    }

    static async fetchCustomerBenefits(customerId) {
        return (await http.get(`/loyalty/benefits?customer_id=${encodeURIComponent(customerId)}`)).data.result
    }

    static async fulfillBenefit(id, reason) {
        return (await http.post(`/loyalty/benefits/${id}/fulfill`, { reason })).data.result
    }

    static async voidBenefit(id, reason) {
        return (await http.post(`/loyalty/benefits/${id}/void`, { reason })).data.result
    }

    static async fetchSettings() {
        try {
            const res = await http.get(`/loyalty/settings`)
            return res.data.result
        } catch (err) {
            console.log("error in fetching loyalty settings", err)
            throw err
        }
    }

    static async updateSettings(payload) {
        try {
            const res = await http.patch(`/loyalty/settings`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in updating loyalty settings", err)
            throw err
        }
    }

    static async fetchTiers() {
        try {
            const res = await http.get(`/loyalty/tiers`)
            return res.data.result
        } catch (err) {
            console.log("error in fetching loyalty tiers", err)
            throw err
        }
    }

    static async createTier(payload) {
        try {
            const res = await http.post(`/loyalty/tiers`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in creating loyalty tier", err)
            throw err
        }
    }

    static async updateTier(id, payload) {
        try {
            const res = await http.patch(`/loyalty/tiers/${id}`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in updating loyalty tier", err)
            throw err
        }
    }

    static async deactivateTier(id) {
        try {
            const res = await http.post(`/loyalty/tiers/${id}/deactivate`)
            return res.data.result
        } catch (err) {
            console.log("error in deactivating loyalty tier", err)
            throw err
        }
    }

    static async fetchRewardItems(search = "", warehouseId = "") {
        try {
            const params = new URLSearchParams()
            if (search) params.append("search", search)
            if (warehouseId) params.append("warehouse_id", warehouseId)
            const res = await http.get(`/loyalty/reward-items?${params.toString()}`)
            return res.data.result
        } catch (err) {
            console.log("error in fetching loyalty reward items", err)
            throw err
        }
    }

    static async fetchRewards() {
        try {
            const res = await http.get(`/loyalty/rewards`)
            return res.data.result
        } catch (err) {
            console.log("error in fetching loyalty rewards", err)
            throw err
        }
    }

    static async createReward(payload) {
        try {
            const res = await http.post(`/loyalty/rewards`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in creating loyalty reward", err)
            throw err
        }
    }

    static async updateReward(id, payload) {
        try {
            const res = await http.patch(`/loyalty/rewards/${id}`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in updating loyalty reward", err)
            throw err
        }
    }

    static async deactivateReward(id) {
        try {
            const res = await http.post(`/loyalty/rewards/${id}/deactivate`)
            return res.data.result
        } catch (err) {
            console.log("error in deactivating loyalty reward", err)
            throw err
        }
    }

    static async fetchCustomerLoyalty(customerId) {
        try {
            const res = await http.get(`/loyalty/accounts/${customerId}`)
            return res.data.result
        } catch (err) {
            console.log("error in fetching customer loyalty", err)
            throw err
        }
    }

    static async fetchCustomerLedger(customerId, page = 1, limit = 25, filters = {}) {
        try {
            const params = new URLSearchParams()
            params.append("page", page)
            params.append("limit", limit)
            Object.entries(filters || {}).forEach(([key, value]) => {
                if (value !== undefined && value !== null && String(value).trim() !== "") {
                    params.append(key, value)
                }
            })
            const res = await http.get(`/loyalty/accounts/${customerId}/ledger?${params.toString()}`)
            return res.data
        } catch (err) {
            console.log("error in fetching customer ledger", err)
            throw err
        }
    }

    static async exportCustomerLedger(customerId, filters = {}) {
        try {
            const params = new URLSearchParams()
            params.append("export", "csv")
            Object.entries(filters || {}).forEach(([key, value]) => {
                if (value !== undefined && value !== null && String(value).trim() !== "") {
                    params.append(key, value)
                }
            })
            const res = await http.get(`/loyalty/accounts/${customerId}/ledger?${params.toString()}`, {
                responseType: "blob",
            })
            return res.data
        } catch (err) {
            console.log("error in exporting customer ledger", err)
            throw err
        }
    }

    static async fetchCustomerRedemptions(customerId) {
        try {
            const res = await http.get(`/loyalty/redemptions?customer_id=${customerId}`)
            return res.data
        } catch (err) {
            console.log("error in fetching customer redemptions", err)
            throw err
        }
    }

    static async adjustPoints(customerId, payload) {
        try {
            const res = await http.post(`/loyalty/accounts/${customerId}/adjust`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in adjusting loyalty points", err)
            throw err
        }
    }

    static async fetchReconciliationIssues(resolved = 0) {
        try {
            const res = await http.get(`/loyalty/reconciliation-issues?resolved=${resolved}`)
            return res.data
        } catch (err) {
            console.log("error in fetching loyalty reconciliation issues", err)
            throw err
        }
    }

    static async fetchHealthReport(date = "", manualAdjustmentThreshold = "") {
        try {
            const params = new URLSearchParams()
            if (date) params.append("date", date)
            if (manualAdjustmentThreshold) params.append("manual_adjustment_threshold", manualAdjustmentThreshold)
            const res = await http.get(`/loyalty/health-report?${params.toString()}`)
            return res.data.result
        } catch (err) {
            console.log("error in fetching loyalty health report", err)
            throw err
        }
    }

    static async fetchCampaigns(filters = {}) {
        try {
            const params = new URLSearchParams()
            Object.entries(filters || {}).forEach(([key, value]) => {
                if (value !== undefined && value !== null && String(value).trim() !== "") {
                    params.append(key, value)
                }
            })
            const res = await http.get(`/loyalty/campaigns?${params.toString()}`)
            return res.data.result
        } catch (err) {
            console.log("error in fetching loyalty campaigns", err)
            throw err
        }
    }

    static async createCampaign(payload) {
        try {
            const res = await http.post(`/loyalty/campaigns`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in creating loyalty campaign", err)
            throw err
        }
    }

    static async updateCampaign(id, payload) {
        try {
            const res = await http.patch(`/loyalty/campaigns/${id}`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in updating loyalty campaign", err)
            throw err
        }
    }

    static async approveCampaign(id, payload) {
        try {
            const res = await http.post(`/loyalty/campaigns/${id}/approve`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in approving loyalty campaign", err)
            throw err
        }
    }

    static async deactivateCampaign(id, payload = {}) {
        try {
            const res = await http.post(`/loyalty/campaigns/${id}/deactivate`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in deactivating loyalty campaign", err)
            throw err
        }
    }

    static async fetchCampaignGrants(id, page = 1, limit = 25) {
        try {
            const res = await http.get(`/loyalty/campaigns/${id}/grants?page=${page}&limit=${limit}`)
            return res.data
        } catch (err) {
            console.log("error in fetching loyalty campaign grants", err)
            throw err
        }
    }

    static async grantCampaignPoints(id, payload) {
        try {
            const res = await http.post(`/loyalty/campaigns/${id}/grants`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in granting loyalty campaign points", err)
            throw err
        }
    }

    static async resolveReconciliationIssue(id, payload = {}) {
        try {
            const res = await http.post(`/loyalty/reconciliation-issues/${id}/resolve`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in resolving loyalty reconciliation issue", err)
            throw err
        }
    }

    static async addReconciliationIssueNote(id, payload = {}) {
        try {
            const res = await http.post(`/loyalty/reconciliation-issues/${id}/notes`, payload)
            return res.data.result
        } catch (err) {
            console.log("error in adding loyalty reconciliation issue note", err)
            throw err
        }
    }

    static async syncCustomerBalance(customerId) {
        try {
            const res = await http.post(`/loyalty/accounts/${customerId}/sync-balance`)
            return res.data.result
        } catch (err) {
            console.log("error in syncing loyalty account balance", err)
            throw err
        }
    }
}

export default LoyaltyFetchApi
